import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  EnvelopeIcon,
  PencilIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Button,
  Badge,
  Modal,
  Input,
  Spinner,
  EmptyState,
  RichTextEditor,
} from '../components/ui';
import { useEmailTemplateStore } from '../stores';
import type { EmailTemplate, TemplateType } from '../types';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailTemplateSchema, type EmailTemplateFormData } from '../utils/validation';

const templateTypeLabels: Record<TemplateType, string> = {
  appointment_reminder: 'Appointment Reminder',
  order_confirmation: 'Order Confirmation',
  shipping_notification: 'Shipping Notification',
  welcome: 'Welcome Email',
  password_reset: 'Password Reset',
  custom: 'Custom Template',
};

const templateTypeColors: Record<TemplateType, 'primary' | 'accent' | 'success' | 'warning' | 'secondary' | 'danger'> = {
  appointment_reminder: 'primary',
  order_confirmation: 'success',
  shipping_notification: 'accent',
  welcome: 'warning',
  password_reset: 'danger',
  custom: 'secondary',
};

export const EmailTemplates: React.FC = () => {
  const { templates, isLoading, fetchTemplates, updateTemplate } = useEmailTemplateStore();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'default' | 'custom'>('all');

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EmailTemplateFormData>({
    resolver: zodResolver(emailTemplateSchema),
  });

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleEditClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    reset({
      name: template.name,
      subject: template.subject,
      body: template.body,
    });
    setEditModalOpen(true);
  };

  const handlePreviewClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setPreviewModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setSelectedTemplate(null);
    reset();
  };

  const onSubmit = async (data: EmailTemplateFormData) => {
    if (!selectedTemplate) return;
    setIsSubmitting(true);
    try {
      await updateTemplate(selectedTemplate.id, data);
      toast.success(
        selectedTemplate.isDefault
          ? 'Template customized for your company'
          : 'Template updated successfully'
      );
      handleCloseEdit();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter and search templates
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        templateTypeLabels[template.type].toLowerCase().includes(searchTerm.toLowerCase());

      // Type filter
      const matchesType = filterType === 'all' ||
        (filterType === 'default' && template.isDefault) ||
        (filterType === 'custom' && !template.isDefault);

      return matchesSearch && matchesType;
    });
  }, [templates, searchTerm, filterType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Email Templates</h1>
        <p className="text-secondary-500 dark:text-secondary-400">Customize your email communications</p>
      </div>

      {/* Search and Filter Controls */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search templates by name, subject, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-secondary-200 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400 dark:placeholder-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'default' | 'custom')}
                className="pl-10 pr-8 py-2 border border-secondary-200 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              >
                <option value="all">All Templates</option>
                <option value="default">Default Templates</option>
                <option value="custom">Custom Templates</option>
              </select>
            </div>
          </div>
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
            Showing {filteredTemplates.length} of {templates.length} templates
          </div>
        )}
      </Card>

      {/* Templates Table */}
      {templates.length === 0 ? (
        <Card>
          <EmptyState
            icon={<EnvelopeIcon className="h-8 w-8" />}
            title="No templates found"
            description="Email templates will appear here"
          />
        </Card>
      ) : filteredTemplates.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-secondary-500 dark:text-secondary-400">No templates match your search criteria.</p>
          <Button
            onClick={() => { setSearchTerm(''); setFilterType('all'); }}
            variant="secondary"
            className="mt-4"
          >
            Clear filters
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Template Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                {filteredTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                            {template.name}
                          </div>
                          {template.isDefault && (
                            <div className="flex items-center mt-1">
                              <DocumentDuplicateIcon className="h-3 w-3 text-secondary-400 mr-1" />
                              <span className="text-xs text-secondary-500 dark:text-secondary-400">Default</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={templateTypeColors[template.type]} className="text-xs">
                        {templateTypeLabels[template.type]}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-secondary-900 dark:text-secondary-100 max-w-xs truncate">
                        {template.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {template.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-400">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 dark:bg-secondary-700 text-secondary-800 dark:text-secondary-400">
                          <XCircleIcon className="h-3 w-3 mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                      {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handlePreviewClick(template)}
                          className="flex items-center gap-1"
                        >
                          <EyeIcon className="h-4 w-4" />
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEditClick(template)}
                          className="flex items-center gap-1"
                        >
                          <PencilIcon className="h-4 w-4" />
                          {template.isDefault ? 'Customize' : 'Edit'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEdit}
        title={selectedTemplate?.isDefault ? 'Customize Email Template' : 'Edit Email Template'}
        size="xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {selectedTemplate?.isDefault && (
            <div className="p-3 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-lg">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                <strong>Note:</strong> This is a default template. Any changes you make will create a customized version for your company. The original default template will remain unchanged.
              </p>
            </div>
          )}

          <Input
            label="Template Name"
            error={errors.name?.message}
            {...register('name')}
            disabled={selectedTemplate?.isDefault}
          />

          <Input
            label="Subject Line"
            error={errors.subject?.message}
            {...register('subject')}
          />

          <Controller
            name="body"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                label="Email Body"
                value={field.value || ''}
                onChange={field.onChange}
                error={errors.body?.message}
                placeholder="Compose your email template..."
              />
            )}
          />

          {selectedTemplate?.variables && selectedTemplate.variables.length > 0 && (
            <div className="p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
              <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Available Variables:</p>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate.variables.map((variable) => (
                  <code
                    key={variable}
                    className="px-2 py-1 text-sm bg-white dark:bg-secondary-700 border border-secondary-200 dark:border-secondary-600 rounded text-secondary-700 dark:text-secondary-300 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(`{{${variable}}}`);
                      toast.info(`Copied {{${variable}}} to clipboard`);
                    }}
                    title="Click to copy"
                  >
                    {`{{${variable}}}`}
                  </code>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseEdit}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {selectedTemplate?.isDefault ? 'Save Customization' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Email Preview"
        size="lg"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-1">Subject:</p>
              <p className="font-medium text-secondary-900 dark:text-secondary-100">{selectedTemplate.subject}</p>
            </div>
            <div className="p-4 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg">
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-2">Body:</p>
              <div
                className="prose prose-sm max-w-none text-secondary-700 dark:text-secondary-300 dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: selectedTemplate.body }}
              />
            </div>
            {selectedTemplate.isDefault && (
              <div className="p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg text-sm text-secondary-600 dark:text-secondary-400">
                This is a default template. Click "Customize" to create your own version.
              </div>
            )}
            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={() => setPreviewModalOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailTemplates;
